import React, { useState, useEffect, useCallback } from "react";
import { api, useBusiness } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import TaxSnapshotCard from "@/components/tax/TaxSnapshotCard";

const formatCurrency = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v || 0);

const QUARTERS = [
  { label: "Q1", due: "Apr 15", months: "Jan-Mar" },
  { label: "Q2", due: "Jun 16", months: "Apr-May" },
  { label: "Q3", due: "Sep 15", months: "Jun-Aug" },
  { label: "Q4", due: "Jan 15", months: "Sep-Dec" },
];

const EstimatedTax = () => {
  const { selectedBusiness } = useBusiness();
  const [snapshot, setSnapshot] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [priorYearTax, setPriorYearTax] = useState("");
  const [activeTab, setActiveTab] = useState("auto");

  const fetchData = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const [latestRes, historyRes] = await Promise.all([
        api.get(`/estimated-tax/${selectedBusiness.id}/latest`),
        api.get(`/estimated-tax/${selectedBusiness.id}`)
      ]);
      setSnapshot(latestRes.data);
      setHistory(historyRes.data);
    } catch { /* no data yet */ }
    finally { setLoading(false); }
  }, [selectedBusiness]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const calculate = async () => {
    setCalculating(true);
    try {
      const params = priorYearTax ? `?prior_year_total_tax=${parseFloat(priorYearTax)}` : "";
      const res = await api.post(`/estimated-tax/calculate/${selectedBusiness.id}${params}`);
      setSnapshot(res.data);
      toast.success("Tax estimate calculated");
      fetchData();
    } catch { toast.error("Calculation failed"); }
    finally { setCalculating(false); }
  };

  const now = new Date();
  const currentQuarter = Math.floor(now.getMonth() / 3);
  const yearProgress = ((now.getMonth() + 1) / 12) * 100;

  if (!selectedBusiness) return <div className="flex items-center justify-center min-h-[400px]"><p className="text-muted-foreground">Please select a business.</p></div>;
  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;

  return (
    <div className="space-y-6" data-testid="estimated-tax-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">Estimated Tax</h1>
          <p className="text-stone-600">Quarterly estimated tax projections and safe harbor targets</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="auto" data-testid="tab-auto">From Transactions</TabsTrigger>
          <TabsTrigger value="whatif" data-testid="tab-whatif">What-If Scenario</TabsTrigger>
        </TabsList>

        {/* AUTO TAB - from transactions */}
        <TabsContent value="auto" className="mt-4 space-y-6">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-stone-500 whitespace-nowrap">Prior Year Tax</label>
              <input type="number" step="0.01" placeholder="$0" value={priorYearTax} onChange={e => setPriorYearTax(e.target.value)}
                className="w-28 h-9 px-2 text-sm border border-stone-200 rounded-md" data-testid="prior-year-tax-input" />
            </div>
            <Button onClick={calculate} disabled={calculating} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="calculate-tax-btn">
              {calculating ? "Calculating..." : "Calculate Now"}
            </Button>
          </div>

          {!snapshot ? (
            <Card><CardContent className="pt-8 pb-8 text-center">
              <p className="text-stone-600 mb-4">No tax estimate yet. Click "Calculate Now" to generate one based on your transactions.</p>
              <Button onClick={calculate} disabled={calculating} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]">
                {calculating ? "Calculating..." : "Generate First Estimate"}
              </Button>
            </CardContent></Card>
          ) : (
            <TaxSnapshotDisplay snapshot={snapshot} history={history} currentQuarter={currentQuarter} yearProgress={yearProgress} />
          )}
        </TabsContent>

        {/* WHAT-IF TAB - direct input */}
        <TabsContent value="whatif" className="mt-4">
          <TaxSnapshotCard />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Extracted snapshot display component
const TaxSnapshotDisplay = ({ snapshot, history, currentQuarter, yearProgress }) => (
  <div className="space-y-6">
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-stone-700">Tax Year {snapshot.tax_year} Progress</span>
          <span className="text-sm text-stone-500">{Math.round(yearProgress)}%</span>
        </div>
        <Progress value={yearProgress} className="h-2" />
        <div className="flex justify-between mt-2">
          {QUARTERS.map((q, i) => (
            <div key={i} className={`text-center ${i === currentQuarter ? "text-[#1e3a5f] font-medium" : "text-stone-400"}`}>
              <p className="text-xs">{q.label}</p>
              <p className="text-[10px]">Due {q.due}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card><CardContent className="pt-5 pb-4 text-center">
        <p className="text-2xl font-bold text-blue-600">{formatCurrency(snapshot.ytd_income)}</p>
        <p className="text-xs text-stone-500 mt-1">YTD Income</p>
      </CardContent></Card>
      <Card><CardContent className="pt-5 pb-4 text-center">
        <p className="text-2xl font-bold text-red-600">{formatCurrency(snapshot.ytd_expenses)}</p>
        <p className="text-xs text-stone-500 mt-1">YTD Expenses</p>
      </CardContent></Card>
      <Card><CardContent className="pt-5 pb-4 text-center">
        <p className={`text-2xl font-bold ${snapshot.ytd_profit >= 0 ? "text-[#1e3a5f]" : "text-red-600"}`}>{formatCurrency(snapshot.ytd_profit)}</p>
        <p className="text-xs text-stone-500 mt-1">YTD Profit</p>
      </CardContent></Card>
      <Card><CardContent className="pt-5 pb-4 text-center">
        <p className="text-2xl font-bold text-stone-800">{formatCurrency(snapshot.projected_annual_profit)}</p>
        <p className="text-xs text-stone-500 mt-1">Projected Annual</p>
      </CardContent></Card>
    </div>

    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Tax Estimate Breakdown</CardTitle>
        <CardDescription>
          Based on your YTD transactions
          {snapshot.entity_type && <span className="ml-1">· Entity: <span className="font-medium capitalize">{snapshot.entity_type.replace(/_/g, " ")}</span></span>}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex justify-between items-center p-3 rounded-lg bg-stone-50">
            <span className="text-sm text-stone-700">Federal Income Tax</span>
            <span className="font-semibold">{formatCurrency(snapshot.estimated_federal_tax)}</span>
          </div>
          <div className="flex justify-between items-center p-3 rounded-lg bg-stone-50">
            <div>
              <span className="text-sm text-stone-700">Self-Employment Tax</span>
              {snapshot.estimated_self_emp_tax === 0 && <span className="text-xs text-stone-400 ml-2">(N/A for this entity type)</span>}
            </div>
            <span className="font-semibold">{formatCurrency(snapshot.estimated_self_emp_tax)}</span>
          </div>
          <div className="flex justify-between items-center p-3 rounded-lg bg-[#1e3a5f]/5 border border-[#1e3a5f]/20">
            <span className="text-sm font-semibold text-[#1e3a5f]">Total Estimated Tax</span>
            <span className="text-lg font-bold text-[#1e3a5f]">{formatCurrency(snapshot.total_estimated_tax)}</span>
          </div>
        </div>
      </CardContent>
    </Card>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card className="border-amber-200 bg-amber-50"><CardContent className="pt-5 pb-4 text-center">
        <p className="text-2xl font-bold text-amber-700">{formatCurrency(snapshot.next_quarter_target)}</p>
        <p className="text-xs text-amber-600 mt-1">Next Quarterly Payment</p>
      </CardContent></Card>
      <Card className="border-blue-200 bg-blue-50"><CardContent className="pt-5 pb-4 text-center">
        <p className="text-2xl font-bold text-blue-700">{formatCurrency(snapshot.safe_harbor_target)}</p>
        <p className="text-xs text-blue-600 mt-1">Safe Harbor Target</p>
      </CardContent></Card>
      <Card><CardContent className="pt-5 pb-4 text-center">
        <p className="text-2xl font-bold text-stone-800">{snapshot.recommended_reserve_pct}%</p>
        <p className="text-xs text-stone-500 mt-1">Recommended Reserve %</p>
      </CardContent></Card>
    </div>

    {history.length > 1 && (
      <Card>
        <CardHeader><CardTitle className="text-lg">Calculation History</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {history.map((s, i) => (
              <div key={s.id} className={`flex items-center justify-between p-3 rounded-lg border ${i === 0 ? "border-[#1e3a5f]/20 bg-[#1e3a5f]/5/30" : "border-stone-200"}`}>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-stone-500">{new Date(s.created_at).toLocaleDateString()}</span>
                  {i === 0 && <Badge className="bg-[#1e3a5f]/10 text-[#1e3a5f] text-[10px]">Latest</Badge>}
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-stone-500">Profit: {formatCurrency(s.ytd_profit)}</span>
                  <span className="font-semibold">Tax: {formatCurrency(s.total_estimated_tax)}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )}

    {/* IRS Filing Resources */}
    <Card data-testid="irs-resources-card">
      <CardHeader>
        <CardTitle className="text-lg">Pay Your Estimated Taxes</CardTitle>
        <CardDescription>Ready to file? These links take you directly to the IRS website where you can pay securely from your bank account.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="p-3 mb-4 rounded-lg bg-blue-50 border border-blue-200 flex items-start gap-2.5">
          <svg className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <p className="text-xs text-blue-800">You'll be redirected to the official IRS website to complete your payment. Opturna calculates your estimated taxes — the IRS handles the actual payment securely through your bank.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a href="https://www.irs.gov/payments/direct-pay" target="_blank" rel="noopener noreferrer" className="group p-4 rounded-xl border border-stone-200 hover:border-[#1e3a5f]/30 hover:bg-[#1e3a5f]/5 transition-colors" data-testid="irs-direct-pay-link">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-[#1e3a5f]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>
              <h4 className="font-semibold text-sm text-[#1e3a5f]">IRS Direct Pay</h4>
              <svg className="w-3.5 h-3.5 text-stone-400 group-hover:text-[#1e3a5f] ml-auto transition-colors" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" /></svg>
            </div>
            <p className="text-xs text-stone-500">Pay directly from your bank account on the IRS website. Free, secure, and instant confirmation.</p>
          </a>
          <a href="https://www.eftps.gov" target="_blank" rel="noopener noreferrer" className="group p-4 rounded-xl border border-stone-200 hover:border-[#1e3a5f]/30 hover:bg-[#1e3a5f]/5 transition-colors" data-testid="irs-eftps-link">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-[#1e3a5f]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" /></svg>
              <h4 className="font-semibold text-sm text-[#1e3a5f]">EFTPS</h4>
              <svg className="w-3.5 h-3.5 text-stone-400 group-hover:text-[#1e3a5f] ml-auto transition-colors" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" /></svg>
            </div>
            <p className="text-xs text-stone-500">Schedule payments in advance on the IRS site and track your full payment history.</p>
          </a>
          <a href="https://www.irs.gov/businesses/small-businesses-self-employed/estimated-taxes" target="_blank" rel="noopener noreferrer" className="group p-4 rounded-xl border border-stone-200 hover:border-[#1e3a5f]/30 hover:bg-[#1e3a5f]/5 transition-colors" data-testid="irs-estimated-tax-link">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-[#1e3a5f]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" /></svg>
              <h4 className="font-semibold text-sm text-[#1e3a5f]">IRS Estimated Tax Guide</h4>
              <svg className="w-3.5 h-3.5 text-stone-400 group-hover:text-[#1e3a5f] ml-auto transition-colors" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" /></svg>
            </div>
            <p className="text-xs text-stone-500">Official IRS guide on who must pay, when it's due, and how to calculate your estimated taxes.</p>
          </a>
        </div>
      </CardContent>
    </Card>
  </div>
);

export default EstimatedTax;
